export interface Problem {
  id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  tags: string[];
  solution: string;
  hints: string[];
  starterCode: string;
}

export const linkedListProblems: Problem[] = [
  {
    id: 'linked-list-1',
    title: 'Reverse Linked List',
    description: `Write a function to reverse a singly linked list.

**Example:**
Input: 1->2->3->4->5->NULL
Output: 5->4->3->2->1->NULL

**Follow up:**
A linked list can be reversed either iteratively or recursively. Could you implement both?

**Note:**
The LinkedListNode class is defined as:
\`\`\`
class ListNode {
  val: number;
  next: ListNode | null;
  constructor(val?: number, next?: ListNode | null) {
    this.val = val === undefined ? 0 : val;
    this.next = next === undefined ? null : next;
  }
}
\`\`\``,
    difficulty: 'Easy',
    tags: ['linked-list', 'recursion'],
    solution: `// Iterative solution
function reverseList(head) {
  let prev = null;
  let current = head;
  
  while (current !== null) {
    // Save next node
    const next = current.next;
    // Reverse the pointer
    current.next = prev;
    // Move pointers forward
    prev = current;
    current = next;
  }
  
  return prev; // New head of the reversed list
}

// Recursive solution
function reverseListRecursive(head) {
  // Base case: empty list or list with only one node
  if (head === null || head.next === null) {
    return head;
  }
  
  // Reverse the rest of the list
  const newHead = reverseListRecursive(head.next);
  
  // Reverse the pointer of the next node
  head.next.next = head;
  head.next = null;
  
  return newHead;
}`,
    hints: [
      'Think about iteratively changing the next pointers of each node.',
      'For the iterative approach, you need three pointers: prev, current, and next.',
      'For the recursive approach, think about how to reverse the rest of the list and then fix the current node.'
    ],
    starterCode: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *   val: number;
 *   next: ListNode | null;
 *   constructor(val?: number, next?: ListNode | null) {
 *     this.val = val === undefined ? 0 : val;
 *     this.next = next === undefined ? null : next;
 *   }
 * }
 */

function reverseList(head) {
  // Your code here
}

// Helper function to create a linked list from array
function createLinkedList(arr) {
  if (arr.length === 0) return null;
  let head = new ListNode(arr[0]);
  let current = head;
  for (let i = 1; i < arr.length; i++) {
    current.next = new ListNode(arr[i]);
    current = current.next;
  }
  return head;
}

// Helper function to convert linked list to array (for testing)
function linkedListToArray(head) {
  const result = [];
  let current = head;
  while (current !== null) {
    result.push(current.val);
    current = current.next;
  }
  return result;
}

// Test your solution
const list = createLinkedList([1, 2, 3, 4, 5]);
const reversed = reverseList(list);
console.log(linkedListToArray(reversed)); // Should print [5, 4, 3, 2, 1]`
  },
  {
    id: 'linked-list-2',
    title: 'Detect Cycle in a Linked List',
    description: `Given \`head\`, the head of a linked list, determine if the linked list has a cycle in it.

There is a cycle in a linked list if there is some node in the list that can be reached again by continuously following the \`next\` pointer. Internally, \`pos\` is used to denote the index of the node that tail's \`next\` pointer is connected to. **Note that \`pos\` is not passed as a parameter**.

Return \`true\` if there is a cycle in the linked list. Otherwise, return \`false\`.

**Example 1:**
Input: head = [3,2,0,-4], pos = 1
Output: true
Explanation: There is a cycle in the linked list, where the tail connects to the 1st node (0-indexed).

**Example 2:**
Input: head = [1,2], pos = 0
Output: true
Explanation: There is a cycle in the linked list, where the tail connects to the 0th node.

**Example 3:**
Input: head = [1], pos = -1
Output: false
Explanation: There is no cycle in the linked list.

**Constraints:**
- The number of the nodes in the list is in the range [0, 10^4].
- -10^5 <= Node.val <= 10^5
- pos is -1 or a valid index in the linked-list.

**Follow up:** Can you solve it using O(1) (i.e. constant) memory?`,
    difficulty: 'Easy',
    tags: ['linked-list', 'two-pointers', 'hash-table'],
    solution: `// Solution 1: Using a hash set (O(n) time, O(n) space)
function hasCycle(head) {
  const visited = new Set();
  let current = head;
  
  while (current !== null) {
    if (visited.has(current)) {
      return true; // Cycle detected
    }
    
    visited.add(current);
    current = current.next;
  }
  
  return false; // No cycle found
}

// Solution 2: Floyd's Cycle-Finding Algorithm (O(n) time, O(1) space)
function hasCycleOptimal(head) {
  if (head === null || head.next === null) {
    return false;
  }
  
  let slow = head;
  let fast = head;
  
  // Fast pointer moves twice as fast as slow pointer
  while (fast !== null && fast.next !== null) {
    slow = slow.next;       // Move slow pointer one step
    fast = fast.next.next;  // Move fast pointer two steps
    
    // If they meet, there's a cycle
    if (slow === fast) {
      return true;
    }
  }
  
  return false; // Fast pointer reached the end, no cycle
}`,
    hints: [
      'If there is no cycle, the fast pointer will eventually reach the end of the list.',
      'If there is a cycle, the fast pointer will eventually meet the slow pointer inside the cycle.',
      'Think about using a hash set to keep track of nodes you\'ve already seen.'
    ],
    starterCode: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *   val: number;
 *   next: ListNode | null;
 *   constructor(val?: number, next?: ListNode | null) {
 *     this.val = val === undefined ? 0 : val;
 *     this.next = next === undefined ? null : next;
 *   }
 * }
 */

function hasCycle(head) {
  // Your code here
}

// Helper function to create a linked list with a cycle
function createLinkedListWithCycle(arr, pos) {
  if (arr.length === 0) return null;
  
  let head = new ListNode(arr[0]);
  let current = head;
  const nodes = [head];
  
  for (let i = 1; i < arr.length; i++) {
    current.next = new ListNode(arr[i]);
    current = current.next;
    nodes.push(current);
  }
  
  if (pos >= 0 && pos < arr.length) {
    current.next = nodes[pos]; // Create cycle
  }
  
  return head;
}

// Test your solution
const noCycleList = createLinkedListWithCycle([1, 2, 3, 4], -1);
console.log(hasCycle(noCycleList)); // Should print false

const cycleList = createLinkedListWithCycle([3, 2, 0, -4], 1);
console.log(hasCycle(cycleList)); // Should print true`
  },
  {
    id: 'linked-list-3',
    title: 'Merge Two Sorted Lists',
    description: `You are given the heads of two sorted linked lists \`list1\` and \`list2\`.

Merge the two lists in a one sorted list. The list should be made by splicing together the nodes of the first two lists.

Return the head of the merged linked list.

**Example 1:**
Input: list1 = [1,2,4], list2 = [1,3,4]
Output: [1,1,2,3,4,4]

**Example 2:**
Input: list1 = [], list2 = []
Output: []

**Example 3:**
Input: list1 = [], list2 = [0]
Output: [0]

**Constraints:**
- The number of nodes in both lists is in the range [0, 50].
- -100 <= Node.val <= 100
- Both list1 and list2 are sorted in non-decreasing order.`,
    difficulty: 'Easy',
    tags: ['linked-list', 'recursion'],
    solution: `// Iterative solution
function mergeTwoLists(list1, list2) {
  // Create a dummy head to make merging easier
  const dummy = new ListNode(-1);
  let current = dummy;
  
  // Traverse both lists and insert smaller value first
  while (list1 !== null && list2 !== null) {
    if (list1.val <= list2.val) {
      current.next = list1;
      list1 = list1.next;
    } else {
      current.next = list2;
      list2 = list2.next;
    }
    current = current.next;
  }
  
  // Attach remaining nodes from list1 or list2
  current.next = list1 !== null ? list1 : list2;
  
  return dummy.next;
}

// Recursive solution
function mergeTwoListsRecursive(list1, list2) {
  // Base cases
  if (list1 === null) return list2;
  if (list2 === null) return list1;
  
  // Decide which node to use as the next node in the result
  if (list1.val <= list2.val) {
    list1.next = mergeTwoListsRecursive(list1.next, list2);
    return list1;
  } else {
    list2.next = mergeTwoListsRecursive(list1, list2.next);
    return list2;
  }
}`,
    hints: [
      'Create a dummy node to start the result list.',
      'Compare the values of the two lists and add the smaller one to the result.',
      'For the recursive approach, think about which node should be the head of the merged list at each step.'
    ],
    starterCode: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *   val: number;
 *   next: ListNode | null;
 *   constructor(val?: number, next?: ListNode | null) {
 *     this.val = val === undefined ? 0 : val;
 *     this.next = next === undefined ? null : next;
 *   }
 * }
 */

function mergeTwoLists(list1, list2) {
  // Your code here
}

// Helper function to create a linked list from array
function createLinkedList(arr) {
  if (arr.length === 0) return null;
  let head = new ListNode(arr[0]);
  let current = head;
  for (let i = 1; i < arr.length; i++) {
    current.next = new ListNode(arr[i]);
    current = current.next;
  }
  return head;
}

// Helper function to convert linked list to array (for testing)
function linkedListToArray(head) {
  const result = [];
  let current = head;
  while (current !== null) {
    result.push(current.val);
    current = current.next;
  }
  return result;
}

// Test your solution
const list1 = createLinkedList([1, 2, 4]);
const list2 = createLinkedList([1, 3, 4]);
const merged = mergeTwoLists(list1, list2);
console.log(linkedListToArray(merged)); // Should print [1, 1, 2, 3, 4, 4]`
  },
  {
    id: 'linked-list-4',
    title: 'Remove Nth Node From End of List',
    description: `Given the \`head\` of a linked list, remove the \`nth\` node from the end of the list and return its head.

**Example 1:**
Input: head = [1,2,3,4,5], n = 2
Output: [1,2,3,5]

**Example 2:**
Input: head = [1], n = 1
Output: []

**Example 3:**
Input: head = [1,2], n = 1
Output: [1]

**Constraints:**
- The number of nodes in the list is \`sz\`.
- 1 <= sz <= 30
- 0 <= Node.val <= 100
- 1 <= n <= sz

**Follow up:** Could you do this in one pass?`,
    difficulty: 'Medium',
    tags: ['linked-list', 'two-pointers'],
    solution: `// Two-pass solution
function removeNthFromEnd(head, n) {
  // First pass: count the length of the list
  let length = 0;
  let current = head;
  while (current !== null) {
    length++;
    current = current.next;
  }
  
  // Calculate the position to remove from the beginning
  const posToRemove = length - n;
  
  // If removing the head
  if (posToRemove === 0) {
    return head.next;
  }
  
  // Second pass: remove the node
  current = head;
  for (let i = 0; i < posToRemove - 1; i++) {
    current = current.next;
  }
  
  // Skip the node to remove
  if (current.next !== null) {
    current.next = current.next.next;
  }
  
  return head;
}

// One-pass solution using two pointers
function removeNthFromEndOnePast(head, n) {
  // Create a dummy node to handle edge cases
  const dummy = new ListNode(0);
  dummy.next = head;
  
  let fast = dummy;
  let slow = dummy;
  
  // Move fast pointer n+1 steps ahead
  for (let i = 0; i <= n; i++) {
    if (fast === null) return null; // Handle edge case
    fast = fast.next;
  }
  
  // Move both pointers until fast reaches the end
  while (fast !== null) {
    slow = slow.next;
    fast = fast.next;
  }
  
  // Remove the nth node from end
  slow.next = slow.next.next;
  
  return dummy.next;
}`,
    hints: [
      'Consider using a dummy node at the beginning of the list to simplify handling edge cases.',
      'Use two pointers with a gap of n between them. When the fast pointer reaches the end, the slow pointer will be at the node before the one to be removed.',
      'Be careful to handle cases like removing the first node or when the list has exactly n nodes.'
    ],
    starterCode: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *   val: number;
 *   next: ListNode | null;
 *   constructor(val?: number, next?: ListNode | null) {
 *     this.val = val === undefined ? 0 : val;
 *     this.next = next === undefined ? null : next;
 *   }
 * }
 */

function removeNthFromEnd(head, n) {
  // Your code here
}

// Helper function to create a linked list from array
function createLinkedList(arr) {
  if (arr.length === 0) return null;
  let head = new ListNode(arr[0]);
  let current = head;
  for (let i = 1; i < arr.length; i++) {
    current.next = new ListNode(arr[i]);
    current = current.next;
  }
  return head;
}

// Helper function to convert linked list to array (for testing)
function linkedListToArray(head) {
  const result = [];
  let current = head;
  while (current !== null) {
    result.push(current.val);
    current = current.next;
  }
  return result;
}

// Test your solution
const list = createLinkedList([1, 2, 3, 4, 5]);
const result = removeNthFromEnd(list, 2);
console.log(linkedListToArray(result)); // Should print [1, 2, 3, 5]`
  },
  {
    id: 'linked-list-5',
    title: 'Palindrome Linked List',
    description: `Given the \`head\` of a singly linked list, return \`true\` if it is a palindrome.

**Example 1:**
Input: head = [1,2,2,1]
Output: true

**Example 2:**
Input: head = [1,2]
Output: false

**Constraints:**
- The number of nodes in the list is in the range [1, 10^5].
- 0 <= Node.val <= 9

**Follow up:** Could you do it in O(n) time and O(1) space?`,
    difficulty: 'Easy',
    tags: ['linked-list', 'two-pointers', 'stack'],
    solution: `// Solution 1: Convert to array (O(n) time, O(n) space)
function isPalindrome(head) {
  const values = [];
  let current = head;
  
  // Copy values to array
  while (current !== null) {
    values.push(current.val);
    current = current.next;
  }
  
  // Check if the array is a palindrome
  let left = 0;
  let right = values.length - 1;
  
  while (left < right) {
    if (values[left] !== values[right]) {
      return false;
    }
    left++;
    right--;
  }
  
  return true;
}

// Solution 2: Two-pointer approach (O(n) time, O(1) space)
function isPalindromeOptimal(head) {
  if (head === null || head.next === null) {
    return true;
  }
  
  // Find the middle of the linked list
  let slow = head;
  let fast = head;
  
  while (fast.next !== null && fast.next.next !== null) {
    slow = slow.next;
    fast = fast.next.next;
  }
  
  // Reverse the second half of the list
  let secondHalf = reverseList(slow.next);
  let firstHalf = head;
  
  // Compare the first and second half
  while (secondHalf !== null) {
    if (firstHalf.val !== secondHalf.val) {
      return false;
    }
    firstHalf = firstHalf.next;
    secondHalf = secondHalf.next;
  }
  
  return true;
}

// Helper function to reverse a linked list
function reverseList(head) {
  let prev = null;
  let current = head;
  
  while (current !== null) {
    const next = current.next;
    current.next = prev;
    prev = current;
    current = next;
  }
  
  return prev;
}`,
    hints: [
      'An easy approach is to copy the values into an array and check if the array is a palindrome.',
      'For the O(1) space solution, find the middle of the linked list, reverse the second half, and compare with the first half.',
      'Don\'t forget to revert the reversed half back if you want to restore the original structure (though not necessary for this problem).'
    ],
    starterCode: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *   val: number;
 *   next: ListNode | null;
 *   constructor(val?: number, next?: ListNode | null) {
 *     this.val = val === undefined ? 0 : val;
 *     this.next = next === undefined ? null : next;
 *   }
 * }
 */

function isPalindrome(head) {
  // Your code here
}

// Helper function to create a linked list from array
function createLinkedList(arr) {
  if (arr.length === 0) return null;
  let head = new ListNode(arr[0]);
  let current = head;
  for (let i = 1; i < arr.length; i++) {
    current.next = new ListNode(arr[i]);
    current = current.next;
  }
  return head;
}

// Test your solution
const palindromeList = createLinkedList([1, 2, 2, 1]);
console.log(isPalindrome(palindromeList)); // Should print true

const nonPalindromeList = createLinkedList([1, 2, 3]);
console.log(isPalindrome(nonPalindromeList)); // Should print false`
  },
  {
    id: 'linked-list-6',
    title: 'Intersection of Two Linked Lists',
    description: `Given the heads of two singly linked-lists \`headA\` and \`headB\`, return the node at which the two lists intersect. If the two linked lists have no intersection at all, return \`null\`.

For example, the following two linked lists begin to intersect at node c1:

A: a1 → a2 → c1 → c2 → c3
B: b1 → b2 → b3 → c1 → c2 → c3

The test cases are generated such that there are no cycles anywhere in the entire linked structure.

**Note** that the linked lists must **retain their original structure** after the function returns.

**Example 1:**

A: 4 → 1 → 8 → 4 → 5
B: 5 → 6 → 1 → 8 → 4 → 5

Input: intersectVal = 8, listA = [4,1,8,4,5], listB = [5,6,1,8,4,5], skipA = 2, skipB = 3
Output: Intersected at '8'
Explanation: The intersected node's value is 8 (note that this must not be 0 if the two lists intersect).
From the head of A, it reads as [4,1,8,4,5]. From the head of B, it reads as [5,6,1,8,4,5]. There are 2 nodes before the intersected node in A; There are 3 nodes before the intersected node in B.

**Example 2:**

A: 1 → 9 → 1 → 2 → 4
B: 3 → 2 → 4

Input: intersectVal = 2, listA = [1,9,1,2,4], listB = [3,2,4], skipA = 3, skipB = 1
Output: Intersected at '2'

**Example 3:**

A: 2 → 6 → 4
B: 1 → 5

Input: intersectVal = 0, listA = [2,6,4], listB = [1,5], skipA = 3, skipB = 2
Output: No intersection

**Constraints:**
- The number of nodes of listA is in the m.
- The number of nodes of listB is in the n.
- 1 <= m, n <= 3 * 10^4
- 1 <= Node.val <= 10^5
- 0 <= skipA < m
- 0 <= skipB < n
- intersectVal is 0 if listA and listB do not intersect.
- intersectVal == listA[skipA] == listB[skipB] if listA and listB intersect.

**Follow up:** Could you write a solution that runs in O(m + n) time and use only O(1) memory?`,
    difficulty: 'Medium',
    tags: ['linked-list', 'two-pointers', 'hash-table'],
    solution: `// Solution 1: Using a hash set (O(m+n) time, O(m) space)
function getIntersectionNode(headA, headB) {
  const visited = new Set();
  
  // Add all nodes from list A to a set
  let currentA = headA;
  while (currentA !== null) {
    visited.add(currentA);
    currentA = currentA.next;
  }
  
  // Check each node in list B for presence in the set
  let currentB = headB;
  while (currentB !== null) {
    if (visited.has(currentB)) {
      return currentB; // Intersection found
    }
    currentB = currentB.next;
  }
  
  return null; // No intersection
}

// Solution 2: Two-pointer technique (O(m+n) time, O(1) space)
function getIntersectionNodeOptimal(headA, headB) {
  if (headA === null || headB === null) {
    return null;
  }
  
  let pointerA = headA;
  let pointerB = headB;
  
  // If a pointer reaches the end, redirect it to the head of the other list
  // Both pointers will travel the same distance before meeting at the intersection
  while (pointerA !== pointerB) {
    // When pointerA reaches the end, redirect to headB
    pointerA = pointerA === null ? headB : pointerA.next;
    
    // When pointerB reaches the end, redirect to headA
    pointerB = pointerB === null ? headA : pointerB.next;
  }
  
  // Either at intersection point or both null if no intersection
  return pointerA;
}`,
    hints: [
      'You can use a hash set to store all nodes of one list and then check for matches in the other list.',
      'For the two-pointer approach, think of making both pointers travel the same distance.',
      'When a pointer reaches the end of its list, redirect it to the head of the other list. Both pointers will meet at the intersection or at null if there\'s no intersection.'
    ],
    starterCode: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *   val: number;
 *   next: ListNode | null;
 *   constructor(val?: number, next?: ListNode | null) {
 *     this.val = val === undefined ? 0 : val;
 *     this.next = next === undefined ? null : next;
 *   }
 * }
 */

function getIntersectionNode(headA, headB) {
  // Your code here
}

// Helper function to create linked lists with intersection
function createIntersectingLists(listA, listB, intersectPos) {
  const nodeA = createLinkedList(listA);
  const nodeB = createLinkedList(listB);
  
  if (intersectPos === -1) {
    return { headA: nodeA, headB: nodeB }; // No intersection
  }
  
  // Create intersection point
  let intersectionNode = nodeA;
  for (let i = 0; i < intersectPos && intersectionNode !== null; i++) {
    intersectionNode = intersectionNode.next;
  }
  
  if (intersectionNode === null) {
    return { headA: nodeA, headB: nodeB }; // Invalid intersect position
  }
  
  // Find tail of nodeB
  let tailB = nodeB;
  while (tailB.next !== null) {
    tailB = tailB.next;
  }
  
  // Connect tailB to intersection node
  tailB.next = intersectionNode;
  
  return { headA: nodeA, headB: nodeB };
}

// Helper function to create a linked list from array
function createLinkedList(arr) {
  if (arr.length === 0) return null;
  let head = new ListNode(arr[0]);
  let current = head;
  for (let i = 1; i < arr.length; i++) {
    current.next = new ListNode(arr[i]);
    current = current.next;
  }
  return head;
}

// Test your solution
const { headA, headB } = createIntersectingLists([4, 1, 8, 4, 5], [5, 6, 1], 2);
const intersection = getIntersectionNode(headA, headB);
console.log(intersection ? intersection.val : null); // Should print 8`
  },
  {
    id: 'linked-list-7',
    title: 'Remove Duplicates from Sorted List',
    description: `Given the \`head\` of a sorted linked list, delete all duplicates such that each element appears only once. Return the linked list sorted as well.

**Example 1:**
Input: head = [1,1,2]
Output: [1,2]

**Example 2:**
Input: head = [1,1,2,3,3]
Output: [1,2,3]

**Constraints:**
- The number of nodes in the list is in the range [0, 300].
- -100 <= Node.val <= 100
- The list is guaranteed to be sorted in ascending order.`,
    difficulty: 'Easy',
    tags: ['linked-list'],
    solution: `function deleteDuplicates(head) {
  // Handle empty list or list with just one node
  if (head === null || head.next === null) {
    return head;
  }
  
  let current = head;
  
  // Traverse the list
  while (current !== null && current.next !== null) {
    // If current node's value is same as next node's value
    if (current.val === current.next.val) {
      // Skip the next node by changing the pointer
      current.next = current.next.next;
    } else {
      // Move to the next node
      current = current.next;
    }
  }
  
  return head;
}`,
    hints: [
      'Since the list is already sorted, you only need to check adjacent nodes for duplicates.',
      'Keep a pointer to the current node and check if its value matches the next node.',
      'If there is a match, change the next pointer to skip the duplicate node.'
    ],
    starterCode: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *   val: number;
 *   next: ListNode | null;
 *   constructor(val?: number, next?: ListNode | null) {
 *     this.val = val === undefined ? 0 : val;
 *     this.next = next === undefined ? null : next;
 *   }
 * }
 */

function deleteDuplicates(head) {
  // Your code here
}

// Helper function to create a linked list from array
function createLinkedList(arr) {
  if (arr.length === 0) return null;
  let head = new ListNode(arr[0]);
  let current = head;
  for (let i = 1; i < arr.length; i++) {
    current.next = new ListNode(arr[i]);
    current = current.next;
  }
  return head;
}

// Helper function to convert linked list to array (for testing)
function linkedListToArray(head) {
  const result = [];
  let current = head;
  while (current !== null) {
    result.push(current.val);
    current = current.next;
  }
  return result;
}

// Test your solution
const list = createLinkedList([1, 1, 2, 3, 3]);
const result = deleteDuplicates(list);
console.log(linkedListToArray(result)); // Should print [1, 2, 3]`
  },
  {
    id: 'linked-list-8',
    title: 'Add Two Numbers',
    description: `You are given two **non-empty** linked lists representing two non-negative integers. The digits are stored in **reverse order**, and each of their nodes contains a single digit. Add the two numbers and return the sum as a linked list.

You may assume the two numbers do not contain any leading zero, except the number 0 itself.

**Example 1:**
Input: l1 = [2,4,3], l2 = [5,6,4]
Output: [7,0,8]
Explanation: 342 + 465 = 807.

**Example 2:**
Input: l1 = [0], l2 = [0]
Output: [0]

**Example 3:**
Input: l1 = [9,9,9,9,9,9,9], l2 = [9,9,9,9]
Output: [8,9,9,9,0,1]
Explanation: 9999999 + 9999 = 10009998

**Constraints:**
- The number of nodes in each linked list is in the range [1, 100].
- 0 <= Node.val <= 9
- It is guaranteed that the list represents a number that does not have leading zeros.`,
    difficulty: 'Medium',
    tags: ['linked-list', 'math', 'recursion'],
    solution: `function addTwoNumbers(l1, l2) {
  // Create a dummy head for the result linked list
  const dummyHead = new ListNode(0);
  let current = dummyHead;
  
  let carry = 0;
  let p1 = l1;
  let p2 = l2;
  
  // Traverse both lists
  while (p1 !== null || p2 !== null) {
    // Get values or use 0 if the list has ended
    const x = p1 !== null ? p1.val : 0;
    const y = p2 !== null ? p2.val : 0;
    
    // Calculate sum and new carry
    const sum = x + y + carry;
    carry = Math.floor(sum / 10);
    
    // Create a new node with the digit value
    current.next = new ListNode(sum % 10);
    current = current.next;
    
    // Move pointers
    if (p1 !== null) p1 = p1.next;
    if (p2 !== null) p2 = p2.next;
  }
  
  // If there's still a carry, add a new node
  if (carry > 0) {
    current.next = new ListNode(carry);
  }
  
  return dummyHead.next;
}`,
    hints: [
      'Keep track of the carry while traversing both lists.',
      'Create a new node for each sum digit.',
      'Be careful with cases where one list is longer than the other, and don\'t forget to add a new node if there\'s a carry after processing all digits.'
    ],
    starterCode: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *   val: number;
 *   next: ListNode | null;
 *   constructor(val?: number, next?: ListNode | null) {
 *     this.val = val === undefined ? 0 : val;
 *     this.next = next === undefined ? null : next;
 *   }
 * }
 */

function addTwoNumbers(l1, l2) {
  // Your code here
}

// Helper function to create a linked list from array
function createLinkedList(arr) {
  if (arr.length === 0) return null;
  let head = new ListNode(arr[0]);
  let current = head;
  for (let i = 1; i < arr.length; i++) {
    current.next = new ListNode(arr[i]);
    current = current.next;
  }
  return head;
}

// Helper function to convert linked list to array (for testing)
function linkedListToArray(head) {
  const result = [];
  let current = head;
  while (current !== null) {
    result.push(current.val);
    current = current.next;
  }
  return result;
}

// Test your solution
const l1 = createLinkedList([2, 4, 3]);
const l2 = createLinkedList([5, 6, 4]);
const sum = addTwoNumbers(l1, l2);
console.log(linkedListToArray(sum)); // Should print [7, 0, 8]`
  }
]; 